import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {formatMessage} from '../fhg/utils/Utils';
import {Button, IconButton} from '@mui/material';
import {makeStyles} from 'tss-react/mui';
import MenuItem from '@mui/material/MenuItem';
import orderBy from 'lodash/orderBy';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TableFHG from '../fhg/components/table/TableFHG';
import TextFieldFHG from './TextFieldFHG';
import TypographyFHG from '../fhg/components/Typography';
import {useIntl} from 'react-intl';
import useLazyQueryFHG from '../fhg/hooks/data/useLazyQueryFHG';
import {FRIENDS_LIST_QUERY, USER_CONNECTIONS_QUERY} from '../data/QueriesGL';

// Friend-sharing statuses (mirror backend FriendSharingStatus enum names).
const STATUS_APPROVED = 'APPROVED';
const STATUS_PENDING = 'PENDING';
const STATUS_REJECTED = 'REJECTED';
const STATUS_CANCELED = 'CANCELED';

const useStyles = makeStyles({name: 'WhiskeyFriendsStyles'})((theme) => ({
  cellStyle: {
    cursor: 'default',
    fontSize: '0.8125rem !important',
  },
  headerStyle: {
    color: theme.palette.primary.contrastText,
    fontSize: '0.75rem !important',
  },
  title: {
    fontSize: '2.0rem',
    margin: theme.spacing(0, 2, 0, 2),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    margin: theme.spacing(0, 2, 1, 2),
  },
  filterRow: {
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    margin: theme.spacing(1, 2, 0.5, 2),
  },
  fieldLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.6875rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  zipField: {
    width: '160px',
  },
  searchField: {
    width: '260px',
  },
  selectStyle: {
    fontSize: '0.875rem',
    height: theme.spacing(4.75),
    width: '150px',
  },
  buttonStyle: {
    backgroundColor: theme.palette.primary.main,
    height: theme.spacing(4.75),
    margin: theme.spacing(0, 0, 0, 1),
  },
  numCell: {
    textAlign: 'right',
  },
  pendingPos: {
    color: '#9a6700',
    fontWeight: 600,
  },
  pendingZero: {
    color: theme.palette.text.disabled,
  },
  // expanded connections panel
  panel: {
    padding: theme.spacing(1, 2, 1.5, 5.5),
  },
  panelTitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    margin: theme.spacing(0.5, 0, 1, 0),
    textTransform: 'uppercase',
  },
  conn: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid rgba(0,0,0,0.12)',
    borderRadius: '5px',
    display: 'flex',
    gap: theme.spacing(1.25),
    margin: theme.spacing(0, 0, 0.75, 0),
    maxWidth: '620px',
    padding: theme.spacing(0.75, 1.25),
  },
  avatar: {
    backgroundColor: '#f3e6d6',
    color: '#b5651d',
    fontSize: '0.6875rem',
    fontWeight: 700,
    height: 28,
    width: 28,
  },
  connName: {
    flex: 1,
    fontSize: '0.8125rem',
    fontWeight: 500,
  },
  connMeta: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginRight: theme.spacing(1),
  },
}));

const DISCOVERABLE_ALL = 'ALL';
const DISCOVERABLE_ON = 'ON';
const DISCOVERABLE_OFF = 'OFF';

function initials(givenName, familyName) {
  const a = (givenName || '').trim();
  const b = (familyName || '').trim();
  const result = `${a ? a[0] : ''}${b ? b[0] : ''}`.toUpperCase();
  return result || '?';
}

/**
 * Lazy-loaded connections for one user, shown inside the expanded table row.
 * Fetches USER_CONNECTIONS_QUERY on mount (i.e. when the row is expanded) and
 * handles loading / error+retry / empty states explicitly.
 */
function ConnectionsPanel({userId}) {
  const {classes} = useStyles();
  const [getConnections, {data, loading, error, called}] = useLazyQueryFHG(
    USER_CONNECTIONS_QUERY, {variables: {userId}}, 'friends.type', false);

  useEffect(() => {
    getConnections({variables: {userId}});
  }, [getConnections, userId]);

  const items = data?.connections?.items || [];

  const statusBadge = useCallback((row) => {
    const sent = row.requesterId === userId;
    switch (row.status) {
      case STATUS_APPROVED:
        return <Chip size='small' label='Friend' style={{backgroundColor: '#e6f4ea', color: '#2e7d32'}} />;
      case STATUS_PENDING:
        return sent
          ? <Chip size='small' variant='outlined' label='Pending — sent' style={{borderColor: '#1565c0', color: '#1565c0'}} />
          : <Chip size='small' label='Pending — received' style={{backgroundColor: '#fff3d6', color: '#9a6700'}} />;
      case STATUS_REJECTED:
        return <Chip size='small' label='Rejected' style={{backgroundColor: '#fdecea', color: '#b71c1c'}} />;
      case STATUS_CANCELED:
        return <Chip size='small' label='Canceled' style={{backgroundColor: '#ececec', color: '#5f5f5f'}} />;
      default:
        return <Chip size='small' label={row.status || 'Unknown'} />;
    }
  }, [userId]);

  if (loading || !called) {
    return (
      <Box className={classes.panel}>
        <Stack direction='row' alignItems='center' spacing={1}>
          <CircularProgress size={16} />
          <TypographyFHG variant='body2'>Loading connections…</TypographyFHG>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.panel}>
        <Alert severity='error'
               action={<Button color='inherit' size='small' onClick={() => getConnections({variables: {userId}})}>Retry</Button>}>
          Could not load connections.
        </Alert>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box className={classes.panel}>
        <TypographyFHG variant='body2' color='textSecondary'>No connections yet.</TypographyFHG>
      </Box>
    );
  }

  const friends = items.filter((i) => i.status === STATUS_APPROVED).length;
  const pending = items.filter((i) => i.status === STATUS_PENDING).length;

  return (
    <Box className={classes.panel}>
      <div className={classes.panelTitle}>
        Connections — {friends} {friends === 1 ? 'friend' : 'friends'}, {pending} pending
      </div>
      {items.map((row) => {
        const cu = row.connectedUser || {};
        return (
          <div className={classes.conn} key={row.sharingId}>
            <Avatar className={classes.avatar}>{initials(cu.givenName, cu.familyName)}</Avatar>
            <span className={classes.connName}>
              {`${cu.givenName || ''} ${cu.familyName || ''}`.trim() || row.connectedUserId}
            </span>
            <span className={classes.connMeta}>{cu.userBottleCount != null ? `${cu.userBottleCount} bottles` : ''}</span>
            {statusBadge(row)}
          </div>
        );
      })}
    </Box>
  );
}

export default function WhiskeyFriends() {
  const {classes} = useStyles();
  const intl = useIntl();

  const [allRows, setAllRows] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [discoverable, setDiscoverable] = useState(DISCOVERABLE_ALL);
  const [zip, setZip] = useState('');
  const [debouncedZip, setDebouncedZip] = useState('');
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const [getFriendsData, {data, loading, error, called}] =
    useLazyQueryFHG(FRIENDS_LIST_QUERY, {variables: {filter: null}}, 'friends.type', true);

  // Build the server-side filter. Discoverable + zip are AND'd server-side so
  // they apply across ALL users (not just the loaded page); allFields is the
  // free-text search. Returns null when nothing is set (browse top 500).
  const buildFilter = useCallback((search, disc, zipValue) => {
    const filter = {};
    const s = (search || '').trim();
    if (s) filter.allFields = {contains: s};
    if (disc === DISCOVERABLE_ON) filter.isLocallyDiscoverable = {eq: true};
    else if (disc === DISCOVERABLE_OFF) filter.isLocallyDiscoverable = {eq: false};
    const z = (zipValue || '').trim();
    if (z) filter.zipcode = {beginsWith: z};
    return Object.keys(filter).length ? filter : null;
  }, []);

  // Debounce zip typing so we fire one server query per pause, not per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedZip(zip.trim()), 400);
    return () => clearTimeout(id);
  }, [zip]);

  // Any change to the committed search, discoverable, or (debounced) zip
  // re-queries the server across the whole user base.
  useEffect(() => {
    setPageIndex(0);
    getFriendsData({variables: {filter: buildFilter(appliedSearch, discoverable, debouncedZip)}});
  }, [getFriendsData, buildFilter, appliedSearch, discoverable, debouncedZip]);

  const handleSearch = useCallback(() => {
    setAppliedSearch(searchText.trim());
  }, [searchText]);

  useMemo(() => {
    if (data && data.users && data.users.items) {
      const sorted = orderBy(data.users.items, [(r) => (r.email || '').toLowerCase()], ['asc']);
      setAllRows(sorted);
    }
  }, [data]);

  const isLoaded = called && !loading && !!data;

  // Filtering is now fully server-side; render the returned rows directly.
  const rows = allRows;
  const isCapped = allRows.length >= 500;
  const hasFilter = discoverable !== DISCOVERABLE_ALL || !!debouncedZip || !!appliedSearch;

  const getPageCount = useCallback((total) => (total > 0 ? Math.ceil(total / pageSize) : 0), [pageSize]);

  const handlePaginationChange = useCallback((obj) => {
    if (pageSize !== obj.pageSize) {
      setPageSize(obj.pageSize);
      setPageIndex(0);
    } else {
      setPageIndex(obj.pageIndex);
    }
  }, [pageSize]);

  const handlePageChange = useCallback((pageNumber) => setPageIndex(pageNumber), []);

  const columns = useMemo(() => ([
    {
      id: 'expander',
      Header: '',
      disableExportValue: true,
      disableSortBy: true,
      Cell: ({row}) => (
        <IconButton size='small' edge='start'
                    onClick={(e) => {e.stopPropagation(); row.toggleRowExpanded();}}>
          {row.isExpanded ? <ExpandMoreIcon fontSize='small' /> : <ChevronRightIcon fontSize='small' />}
        </IconButton>
      ),
    },
    {
      id: 'name',
      Header: 'Name',
      accessor: (r) => `${r.givenName || ''} ${r.familyName || ''}`.trim() || r.username || '',
    },
    {id: 'email', Header: 'Email', accessor: 'email'},
    {
      id: 'discoverable',
      Header: 'Discoverable',
      accessor: (r) => (r.isLocallyDiscoverable ? 'On' : 'Off'),
      Cell: ({row}) => (
        row.original.isLocallyDiscoverable
          ? <Chip size='small' label='On' style={{backgroundColor: '#e6f4ea', color: '#2e7d32', fontWeight: 600}} />
          : <Chip size='small' label='Off' style={{backgroundColor: '#ececec', color: '#5f5f5f', fontWeight: 600}} />
      ),
    },
    {
      id: 'friendCount',
      Header: 'Friends',
      accessor: (r) => r.friendCount ?? 0,
      Cell: ({value}) => <span className={classes.numCell} style={{display: 'block'}}>{value}</span>,
    },
    {
      id: 'pendingCount',
      Header: 'Pending',
      accessor: (r) => r.pendingCount ?? 0,
      Cell: ({value}) => (
        <span className={`${value > 0 ? classes.pendingPos : classes.pendingZero}`}
              style={{display: 'block', textAlign: 'right'}}>{value}</span>
      ),
    },
    {id: 'zipcode', Header: 'Zip', accessor: 'zipcode'},
    {
      id: 'location',
      Header: 'City / State',
      accessor: (r) => [r.city, r.state].filter(Boolean).join(', '),
    },
  ]), [classes.numCell, classes.pendingPos, classes.pendingZero]);

  const renderExpandedRow = useCallback(
    (row) => <ConnectionsPanel userId={row.original.id} />, []);

  return (
    <Stack display='flex' direction='column' height='100%'>
      <TypographyFHG id='friends.title' className={classes.title} />

      <Stack direction='row' spacing={2} className={classes.filterRow}>
        <Stack direction='column'>
          <span className={classes.fieldLabel}>{formatMessage(intl, 'friends.zip.label')}</span>
          <TextFieldFHG name='zip' fullWidth={false} className={classes.zipField}
                        disabled={!isLoaded} value={zip} placeholder='e.g. 90210'
                        size='small' onChange={(e) => { setZip(e.target.value); setPageIndex(0); }} />
        </Stack>
        <Stack direction='column'>
          <span className={classes.fieldLabel}>{formatMessage(intl, 'friends.discoverable.label')}</span>
          <Select className={classes.selectStyle} size='small' disabled={!isLoaded}
                  value={discoverable} onChange={(e) => { setDiscoverable(e.target.value); setPageIndex(0); }}>
            <MenuItem value={DISCOVERABLE_ALL}>All</MenuItem>
            <MenuItem value={DISCOVERABLE_ON}>On</MenuItem>
            <MenuItem value={DISCOVERABLE_OFF}>Off</MenuItem>
          </Select>
        </Stack>
        <Stack direction='column'>
          <span className={classes.fieldLabel}>Search all users</span>
          <Stack direction='row' spacing={1}>
            <TextFieldFHG name='search' fullWidth={false} className={classes.searchField}
                          value={searchText}
                          placeholder={formatMessage(intl, 'friends.search.text')}
                          size='small' onChange={(e) => setSearchText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }} />
            <Button variant='contained' size='small' onClick={handleSearch} disabled={loading}>
              Search
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {error && (
        <Alert severity='error' style={{margin: '8px 16px'}}
               action={<Button color='inherit' size='small' onClick={() => getFriendsData()}>Retry</Button>}>
          <AlertTitle>Could not load users</AlertTitle>
          The user list failed to load. Filters are disabled until it loads.
        </Alert>
      )}

      {!isLoaded && !error && (
        <Stack direction='row' alignItems='center' spacing={1} style={{margin: '16px'}}>
          <CircularProgress size={18} />
          <TypographyFHG variant='body2'>Loading all users…</TypographyFHG>
        </Stack>
      )}

      {isLoaded && (
        <div className={classes.subtitle}>
          Showing {rows.length}{isCapped ? '+' : ''} {hasFilter ? 'matching users' : 'users'}
          {isCapped && ' · showing first 500 — narrow the filters to see more'}
        </div>
      )}

      <TableFHG
        key={`wf-${appliedSearch}-${discoverable}-${debouncedZip}`}
        name='whiskey-friends-grid'
        columns={columns}
        data={rows}
        classes={{headerStyle: classes.headerStyle, cellStyle: classes.cellStyle}}
        dataCount={rows?.length || 0}
        emptyTableMessageKey={'friends.noRecord.message'}
        exportExcel
        exportFileName='whiskey-friends'
        isLoading={!isLoaded}
        onPageChange={handlePageChange}
        onPaginationChange={handlePaginationChange}
        pageCount={getPageCount(rows ? rows.length : 0)}
        pageSize={pageSize}
        pageIndex={pageIndex}
        subTitleKey='friends.subtitle'
        renderExpandedRow={renderExpandedRow}
      />
    </Stack>
  );
}
